import { createElement, useEffect, useRef, useState } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { DEFAULT_INPUT_DEBOUNCE_DELAY } from '../constants';
import { debounce } from '../utilities/debounce';

let globalQueryClient;

/**
 * @param {Object} options
 * @param {string|Array} options.queryKeyPrefix A prefix for React Query's key (e.g. "searchUsers" or ["searchUsers"]).
 * @param {Function}     options.fetchFn        Your fetch function that accepts (fetchArg, signal).
 * @param {string}       options.fetchArg     Raw input from the user.
 * @param {number}       [options.delay=DEFAULT_INPUT_DEBOUNCE_DELAY] Debounce delay (ms).
 * @param {Object}       [options.queryOptions={}] Additional React Query options (enabled, retry, staleTime, etc).
 */
export function useDebouncedQuery({
  queryKeyPrefix = 'debouncedSearch',
  fetchFn,
  fetchArg,
  delay = DEFAULT_INPUT_DEBOUNCE_DELAY,
  queryOptions = {},
}) {
  const [debouncedValue, setDebouncedValue] = useState(fetchArg);
  const debouncedFnRef = useRef(null);

  if (!debouncedFnRef.current) {
    debouncedFnRef.current = debounce((val) => {
      setDebouncedValue(val);
    }, delay);
  }

  useEffect(() => {
    debouncedFnRef.current(fetchArg);

    return () => {
      debouncedFnRef.current.cancel();
    };
  }, [fetchArg]);

  // Ensure QueryClient is available
  let queryClient = useQueryClient();

  if (!queryClient) {
    if (!globalQueryClient) {
      globalQueryClient = new QueryClient();
    }

    queryClient = globalQueryClient;
  }

  const queryKey = Array.isArray(queryKeyPrefix)
    ? [...queryKeyPrefix, debouncedValue]
    : [queryKeyPrefix, debouncedValue];

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchFn(debouncedValue, signal),
    queryClient, // Ensure query uses this client
    ...queryOptions,
  });
}

/**
 * If used without wrapping the app in `QueryClientProvider`, this wrapper will handle it.
 */
export function DebouncedQueryClientWrapper({ children }) {
  if (!globalQueryClient) globalQueryClient = new QueryClient();

  return createElement(
    QueryClientProvider,
    { client: globalQueryClient },
    children
  );
}

DebouncedQueryClientWrapper.propTypes = { children: PropTypes.node.isRequired };
